<script setup lang="ts">
import { CheckCircle2, Info, AlertTriangle, AlertOctagon, X } from 'lucide-vue-next';
import { useToast, type ToastKind } from '@/composables/useToast';

const { toasts, dismiss } = useToast();

const meta: Record<ToastKind, { icon: typeof CheckCircle2; color: string }> = {
  success: { icon: CheckCircle2, color: 'var(--color-success)' },
  info: { icon: Info, color: 'var(--color-info)' },
  warning: { icon: AlertTriangle, color: 'var(--color-warning)' },
  danger: { icon: AlertOctagon, color: 'var(--color-danger)' },
};
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none max-w-[min(380px,calc(100vw-2rem))]"
    >
      <transition-group name="toast" tag="div" class="flex flex-col gap-2">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="card pointer-events-auto p-3 flex items-start gap-3 shadow-2xl"
          :style="{
            background: 'var(--card-bg)',
            borderLeft: `3px solid ${meta[t.kind].color}`,
            boxShadow: 'var(--card-shadow-lift)',
          }"
          role="status"
        >
          <component
            :is="meta[t.kind].icon"
            :size="18"
            :style="{ color: meta[t.kind].color }"
            class="shrink-0 mt-0.5"
          />
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-sm">{{ t.title }}</p>
            <p v-if="t.message" class="text-xs mt-0.5" :style="{ color: 'var(--surface-fg-2)' }">
              {{ t.message }}
            </p>
          </div>
          <button
            class="btn btn-icon btn-ghost"
            :style="{ width: '24px', height: '24px', padding: '4px' }"
            @click="dismiss(t.id)"
            aria-label="Zamknij"
          >
            <X :size="12" />
          </button>
        </div>
      </transition-group>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.3s var(--ease-smooth),
    transform 0.4s var(--ease-spring);
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(20px) scale(0.95);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(20px) scale(0.95);
}
</style>
